import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowDown2, ArrowUp2 } from "iconsax-react";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { FileUploader } from "@/shared/components";
import { useAppSelector, useDocumentSuggest, useFormDraft } from "@/shared/hooks";
import { Button, DatePicker, Input, Toast } from "@/shared/ui";
import { applyOcrFields, formatDateToISO, parseOcrDate, type Locale } from "@/shared/utils";
import { hasAnyValue } from "../partialSave";
import { useGetIdDocumentsQuery, useSubmitIdDocumentsMutation } from "./api";
import type { IdDocumentsDto } from "./types";
import IdDocumentsFormSkeleton from "./IdDocumentsFormSkeleton";
import { idDocumentsSchema, type IdDocumentsFormValues } from "./validation";

interface Props {
  token: string;
  isResident?: boolean;
  onNext: () => void;
}

export default function IdDocumentsForm({ token, isResident, onNext }: Props) {
  const { i18n, t } = useTranslation("ApplyApplicationPage");
  const locale = (i18n.language as Locale) || "ru";
  const isResidentFromRedux = useAppSelector((s) => s.completeness.is_resident);
  const effectiveIsResident = isResident ?? isResidentFromRedux ?? true;
  const docType = effectiveIsResident ? "identity" : "residence_permit";

  const { data, isLoading, error } = useGetIdDocumentsQuery(token);
  const [submitDocuments, { isLoading: isSaving }] = useSubmitIdDocumentsMutation();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [toast, setToast] = useState<{
    text: string;
    color: "positive" | "negative" | "notice" | "grey";
  } | null>(null);

  const [openId, setOpenId] = useState(true);
  const [isNationalFileRemoved, setIsNationalFileRemoved] = useState(false);
  const [ocrFilled, setOcrFilled] = useState<Set<string>>(new Set());
  const ocrDocType = effectiveIsResident ? "national_id" : "passport";
  const { scan: scanDocument, state: ocrState, reset: resetOcr } = useDocumentSuggest(token);

  const methods = useForm<IdDocumentsFormValues>({
    resolver: zodResolver(idDocumentsSchema),
    defaultValues: {
      national_id_number: "",
      national_id_issue_date: null,
      national_id_expiry_date: null,
      national_id_issued_by: "",
      national_id_file: null,
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = methods;

  const { applyDraft, hasValidDraft, clearDraft } = useFormDraft<IdDocumentsFormValues>({
    key: `onboarding:id-documents:${token}`,
    form: methods,
    schemaVersion: "v1",
    dateFields: ["national_id_issue_date", "national_id_expiry_date"],
    ignoreFields: ["national_id_file"],
  });

  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (data && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      reset({
        national_id_number: data.national_id_number ?? "",
        national_id_issue_date: data.national_id_issue_date ? new Date(data.national_id_issue_date) : null,
        national_id_expiry_date: data.national_id_expiry_date ? new Date(data.national_id_expiry_date) : null,
        national_id_issued_by: data.national_id_issued_by ?? "",
        national_id_file: null,
      });
      setIsNationalFileRemoved(false);
      if (hasValidDraft) applyDraft();
    } else if (!data && hasValidDraft) {
      applyDraft();
    }
  }, [data, reset, hasValidDraft, applyDraft]);

  const issueDate = watch("national_id_issue_date");
  const expiryDate = watch("national_id_expiry_date");
  const nationalFile = watch("national_id_file");

  function applyOcrDate(field: "national_id_issue_date" | "national_id_expiry_date", fields: Record<string, string>): boolean {
    if (getValues(field)) return false;
    const parsed = parseOcrDate(fields[field]);
    if (!parsed) return false;
    setValue(field, parsed);
    return true;
  }

  async function runIdOcr(uploadedFile: File) {
    const result = await scanDocument(uploadedFile, ocrDocType);
    if (result.status === "idle") return;
    const fields = result.fields ?? {};

    const matched =
      (fields.national_id_number?.trim() ? 1 : 0) +
      (fields.national_id_issued_by?.trim() ? 1 : 0) +
      (parseOcrDate(fields.national_id_issue_date) ? 1 : 0) +
      (parseOcrDate(fields.national_id_expiry_date) ? 1 : 0);
    if (matched * 2 < 4) {
      setToast({ color: "notice", text: `id.ocr.wrongFile.${ocrDocType}` });
      return;
    }

    const filled: string[] = [];

    const textResult = applyOcrFields(
      {
        national_id_number: getValues("national_id_number") ?? "",
        national_id_issued_by: getValues("national_id_issued_by") ?? "",
      },
      fields,
      ["national_id_number", "national_id_issued_by"],
    );
    for (const key of ["national_id_number", "national_id_issued_by"] as const) {
      if (textResult.filled.includes(key)) {
        setValue(key, textResult.next[key], { shouldValidate: true });
        filled.push(key);
      }
    }

    if (applyOcrDate("national_id_issue_date", fields)) filled.push("national_id_issue_date");
    if (applyOcrDate("national_id_expiry_date", fields)) filled.push("national_id_expiry_date");

    if (filled.length > 0) {
      setOcrFilled(new Set(filled));
      setToast({ color: "positive", text: "id.ocr.filled" });
    }
  }

  function handleNationalFileChange(file: File | null) {
    setValue("national_id_file", file, { shouldValidate: true });
    if (file) {
      setIsNationalFileRemoved(false);
      void runIdOcr(file);
    } else {
      resetOcr();
      setOcrFilled(new Set());
    }
  }

  async function handleSave() {
    if (isSaving) return;
    const values = getValues();

    const partial: Partial<IdDocumentsDto> = {};

    if (values.national_id_number) partial.national_id_number = values.national_id_number;
    if (values.national_id_issue_date) partial.national_id_issue_date = formatDateToISO(values.national_id_issue_date);
    if (values.national_id_expiry_date) partial.national_id_expiry_date = formatDateToISO(values.national_id_expiry_date);
    if (values.national_id_issued_by) partial.national_id_issued_by = values.national_id_issued_by;

    const hasNationalFromApi = !!data?.national_id_file_url && !isNationalFileRemoved;
    if (!hasNationalFromApi && values.national_id_file) partial.national_id_file = values.national_id_file as File;

    if (!hasAnyValue(partial)) return;

    try {
      setIsSavingDraft(true);
      await submitDocuments({ token, body: partial }).unwrap();
      setToast({ color: "positive", text: "id.toast.success" });
    } catch {
      setToast({ color: "negative", text: "id.toast.error" });
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function onSubmit(values: IdDocumentsFormValues) {
    if (isSaving) return;

    try {
      const hasNationalFromApi = !!data?.national_id_file_url && !isNationalFileRemoved;
      const hasNationalNewFile = !!values.national_id_file;

      if (!hasNationalFromApi && !hasNationalNewFile) {
        setToast({ color: "negative", text: "validation.id.file_required" });
        return;
      }

      await submitDocuments({
        token,
        body: {
          national_id_number: values.national_id_number,
          national_id_issue_date: formatDateToISO(values.national_id_issue_date),
          national_id_expiry_date: formatDateToISO(values.national_id_expiry_date),
          national_id_issued_by: values.national_id_issued_by,
          national_id_file: hasNationalFromApi ? null : (values.national_id_file as File | null),
        },
      }).unwrap();

      clearDraft();
      setToast({ color: "positive", text: "id.toast.success" });
      onNext();
    } catch (e) {
      void e;
      setToast({ color: "negative", text: "id.toast.error" });
    }
  }

  const isNotFound = error && typeof error === "object" && "status" in error && error.status === 404;
  if (isLoading && !data && !isNotFound) return <IdDocumentsFormSkeleton />;

  return (
    <form className="flex flex-col justify-between min-h-[640px] gap-7" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-7">
        <h2 className="text-display-2xs content-base-primary">{t("id.title")}</h2>

        <div className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => setOpenId((v) => !v)}
            className="flex justify-between items-center pb-3 border-b surface-base-stroke cursor-pointer">
            <p className="text-body-bold-lg content-base-primary">{t(`id.${docType}.title`)}</p>

            <span className="input-box-shadow radius-2xs w-6 h-6 flex items-center justify-center content-base-secondary">
              {openId ? <ArrowUp2 size={16} color="currentColor" /> : <ArrowDown2 size={16} color="currentColor" />}
            </span>
          </button>

          {openId && (
            <div className="flex flex-col gap-4">
              <FileUploader
                label={t(`id.${docType}.file.label`)}
                hasRequiredLabel
                value={nationalFile}
                existingFileUrl={!isNationalFileRemoved ? data?.national_id_file_url : null}
                onChange={handleNationalFileChange}
                onRemoveExisting={() => {
                  setIsNationalFileRemoved(true);
                  setValue("national_id_file", null);
                  resetOcr();
                  setOcrFilled(new Set());
                }}
                maxSizeMB={50}
                error={errors.national_id_file?.message ? t(errors.national_id_file.message as string) : null}
              />

              {ocrState === "scanning" ? (
                <Toast color="grey" text={t("id.ocr.scanning")} closable={false} autoClose={false} isFullWidth />
              ) : (
                <Toast color="grey" text={t("id.ocr.hint")} closable={false} autoClose={false} isFullWidth />
              )}

              <Input
                label={t(`id.${docType}.number.label`)}
                hasRequiredLabel
                placeholder={t(`id.${docType}.number.placeholder`)}
                highlight={ocrFilled.has("national_id_number")}
                {...register("national_id_number")}
                error={errors.national_id_number?.message ? t(errors.national_id_number.message) : undefined}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DatePicker
                  label={t(`id.${docType}.issue_date.label`)}
                  hasRequiredLabel
                  mode="single"
                  locale={locale}
                  value={issueDate}
                  maxDate={new Date()}
                  placeholder={t(`id.${docType}.issue_date.placeholder`)}
                  highlight={ocrFilled.has("national_id_issue_date")}
                  onChange={(v) => setValue("national_id_issue_date", (v as Date) ?? null, { shouldValidate: true })}
                  error={errors.national_id_issue_date?.message ? t(errors.national_id_issue_date.message as string) : undefined}
                />

                <DatePicker
                  label={t(`id.${docType}.expiry_date.label`)}
                  hasRequiredLabel
                  mode="single"
                  locale={locale}
                  value={expiryDate}
                  placeholder={t(`id.${docType}.expiry_date.placeholder`)}
                  highlight={ocrFilled.has("national_id_expiry_date")}
                  onChange={(v) => setValue("national_id_expiry_date", (v as Date) ?? null, { shouldValidate: true })}
                  error={errors.national_id_expiry_date?.message ? t(errors.national_id_expiry_date.message as string) : undefined}
                />
              </div>

              <Input
                label={t(`id.${docType}.issued_by.label`)}
                hasRequiredLabel
                placeholder={t(`id.${docType}.issued_by.placeholder`)}
                highlight={ocrFilled.has("national_id_issued_by")}
                {...register("national_id_issued_by")}
                error={errors.national_id_issued_by?.message ? t(errors.national_id_issued_by.message) : undefined}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {toast && (
          <Toast
            key={`${toast.color}-${toast.text}`}
            color={toast.color}
            text={t(toast.text)}
            onClose={() => setToast(null)}
          />
        )}

        <Button variant="secondary" size="lg" type="button" onClick={handleSave} disabled={isSaving}>
          {isSavingDraft ? t("id.saving") : t("id.save")}
        </Button>

        <Button variant="primary" size="lg" type="submit" disabled={isSaving}>
          {isSaving && !isSavingDraft ? t("id.saving") : t("id.next_section")}
          {(!isSaving || isSavingDraft) && (
            <span className="text-white">
              <ArrowRightIcon />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
